import { Box, Button, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import ExoForm from '../../components/exo/ExoForm';
import { API_PATCH, DBRequest, useConfig } from '../../api/api';
import { useTranslation } from 'react-i18next';
import findDifferences from '../../components/exo/findDifferencesInDataCollection';

/**
 * The travel expense rates tab: the `travel_expense_*` config rows, which the
 * backend calculator reads for every preview and saved trip.
 *
 * The rows technically live in the Project config category (the settings index
 * routes them here instead of the generic project tab), so this tab is the one
 * place that knows what each rate means: it overrides the raw display names
 * with proper labels, units and an info popper stating the statutory value and
 * whether the employer may deviate from it.
 *
 * Saving PATCHes the generic `config` bulk endpoint keyed by config row id,
 * exactly like DBSettingTab.
 */
const TravelExpenseSettings = ({ data = [], onFeedback = () => {} }) => {
  const { t } = useTranslation();
  const config = useConfig();
  const currencySymbol = config?.currency?.currencySymbol ?? '';

  const formRef = useRef(null);
  const [fields, setFields] = useState([]);
  const [startData, setStartData] = useState({});
  const [validationSchema, setValidationSchema] = useState(null);
  const [valid, setValid] = useState(true);

  useEffect(() => {
    const byKey = {};
    data.forEach((setting) => {
      byKey[setting.key] = setting;
    });

    // Definition per config key: label, input type and the statutory context.
    // Order here is the display order.
    const definitions = [
      { type: 'sectionHeader', label: t('Mileage allowance') },
      {
        key: 'travelExpenseDefaultPricePerKilometer',
        label: t('Price per Kilometer'),
        type: 'number',
        unit: `${currencySymbol}/km`,
        decimalPlaces: 2,
        max: null,
        info: t(
          'Tax-free up to 0.30 € per kilometer by car. A higher rate is allowed, but the difference counts as taxable wages; a lower rate is always allowed.',
        ),
      },
      { type: 'sectionHeader', label: t('Meal allowance') },
      {
        key: 'travelExpensePerDiemFull',
        label: t('Per diem, full day (24h absence)'),
        type: 'money',
        max: null,
        info: t(
          'Statutory tax-free rate: 28.00 €. A higher amount counts as taxable wages; a lower amount is always allowed.',
        ),
      },
      {
        key: 'travelExpensePerDiemPartial',
        label: t('Per diem, arrival/departure day'),
        type: 'money',
        max: null,
        info: t(
          'Statutory tax-free rate: 14.00 €. Also paid for single-day trips over the minimum absence.',
        ),
      },
      {
        key: 'travelExpensePerDiemMinHours',
        label: t('Minimum absence for single-day trips'),
        type: 'number',
        unit: 'h',
        decimalPlaces: 0,
        max: 24,
        info: t('Statutory: more than 8 hours of absence.'),
      },
      {
        key: 'travelExpenseMealDeductionBreakfastPercent',
        label: t('Deduction for provided breakfast'),
        type: 'number',
        unit: '%',
        decimalPlaces: 0,
        max: 100,
        info: t(
          'Statutory: 20% of the full-day rate (5.60 €). The deduction is mandatory whenever the meal is provided.',
        ),
      },
      {
        key: 'travelExpenseMealDeductionLunchPercent',
        label: t('Deduction for provided lunch'),
        type: 'number',
        unit: '%',
        decimalPlaces: 0,
        max: 100,
        info: t(
          'Statutory: 40% of the full-day rate (11.20 €). The deduction is mandatory whenever the meal is provided.',
        ),
      },
      {
        key: 'travelExpenseMealDeductionDinnerPercent',
        label: t('Deduction for provided dinner'),
        type: 'number',
        unit: '%',
        decimalPlaces: 0,
        max: 100,
        info: t(
          'Statutory: 40% of the full-day rate (11.20 €). The deduction is mandatory whenever the meal is provided.',
        ),
      },
      { type: 'sectionHeader', label: t('Accommodation') },
      {
        key: 'travelExpenseOvernightFlatRate',
        label: t('Overnight flat rate'),
        type: 'money',
        max: null,
        info: t(
          'Statutory tax-free flat rate: 20.00 € per night without a receipt. Actual hotel costs are reimbursed as additional costs with a receipt instead.',
        ),
      },
      { type: 'sectionHeader', label: t('Additional costs') },
      {
        key: 'travelExpenseAllowCostsWithoutReceipt',
        label: t('Allow additional costs without receipt'),
        type: 'checkbox',
        info: t(
          'If disabled, every additional cost needs an uploaded receipt before the request can be saved. If enabled, a missing receipt only shows as a warning.',
        ),
      },
    ];

    const newFields = [];
    const newStartData = {};
    const schemaShape = {};

    definitions.forEach((definition) => {
      if (definition.type === 'sectionHeader') {
        newFields.push(definition);
        return;
      }

      const setting = byKey[definition.key];
      // A missing row means the backend config seeder has not run for this
      // key yet; skip the field instead of editing nothing.
      if (!setting) return;

      const { key, max, ...fieldProps } = definition;
      newFields.push({ ...fieldProps, key: setting.id });
      newStartData[setting.id] = setting.value;

      // Checkboxes hold '0'/'1' and need no numeric bounds.
      if (definition.type === 'checkbox') return;

      let rule = Yup.number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .min(0, t('Must be 0 or more'));
      if (max != null) rule = rule.max(max, `${t('Maximum')}: ${max}`);
      schemaShape[setting.id] = rule;
    });

    setFields(newFields);
    setStartData(newStartData);
    setValidationSchema(Yup.object().shape(schemaShape));
    // The currency symbol arrives async with the config, so the field
    // definitions are rebuilt once it is known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, currencySymbol]);

  function handleSubmit() {
    const differences = findDifferences(startData, formRef.current.dataCollection);

    // The bulk config endpoint validates every value as a string; the number
    // fields write real numbers into the collection.
    const reqData = {};
    Object.entries(differences).forEach(([id, value]) => {
      reqData[id] = String(value ?? '');
    });

    DBRequest({
      config,
      path: 'config',
      method: API_PATCH(reqData),
      onResponse: () => {
        setStartData(formRef.current.dataCollection);
      },
      onFeedback,
    });
  }

  return (
    <Box className="h-full w-full p-4 flex flex-col justify-between gap-4">
      <ExoForm
        ref={formRef}
        className="h-full flex flex-col gap-4 overflow-y-auto pt-2"
        fields={fields}
        startDataCollection={startData}
        validationSchema={validationSchema}
        noControls
        onChange={(_, isValid) => setValid(isValid)}
      />

      <Box className="flex flex-row items-center justify-between gap-4">
        <Typography variant="caption" className="opacity-60" sx={{ maxWidth: '640px' }}>
          {t(
            'The statutory values are the caps for tax-free reimbursement (status 2026). Reimbursing less is always allowed; anything above the cap counts as taxable wages. The meal deduction percentages are prescribed by law.',
          )}
        </Typography>
        <Button
          variant="contained"
          color="success"
          disabled={!valid}
          onClick={handleSubmit}
        >
          {t('Save')}
        </Button>
      </Box>
    </Box>
  );
};

export default TravelExpenseSettings;
